<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between gap-4">
      <h2>{{ t('coupons') }}</h2>
      <CVButton v-if="canApplyCoupon" @click.prevent="showApplyCoupon = true">
        {{ t('applyCoupon') }}
      </CVButton>
    </div>

    <div class="space-y-2">
      <StatusBanner v-for="coupon in appliedCoupons" :key="coupon.id" variant="success">
        <span class="inline-flex flex-wrap items-baseline gap-x-2">
          <span class="font-mono font-semibold">{{ coupon.code }}</span>
          <span>{{ t(`couponType.${coupon.type}`) }}</span>
          <span v-if="getCouponAmount(coupon) != null" class="font-medium tabular-nums">
            <span v-currency="getCouponAmount(coupon)!" />
          </span>
        </span>
        <template v-if="canRemoveCoupon(coupon)" #actions>
          <CVButton
            variant="link"
            size="sm"
            :disabled="isRemoving"
            @click.prevent="handleRemove(coupon)"
          >
            {{ t('remove') }}
          </CVButton>
        </template>
      </StatusBanner>
    </div>

    <StatusBanner v-if="error" variant="problem">
      {{ error }}
    </StatusBanner>

    <Modal v-model="showApplyCoupon" show-close>
      <template #title>{{ t('applyCoupon') }}</template>
      <ApplyCoupon
        :booking-id="booking.id"
        @cancel="showApplyCoupon = false"
        @success="showApplyCoupon = false"
      />
    </Modal>

    <PaymentDetailsModal
      v-if="refundPayment"
      :key="refundPayment.id"
      v-model="showRefundModal"
      :payment="refundPayment"
      :booking-id="booking.id"
      initial-show-refund
    />
  </div>
</template>

<script lang="ts" setup>
import { getCustomBookedExtras } from '@/utils/pricing.utils';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRemoveCoupon } from '@/queries/use-coupons';
import { usePaymentsWithQuery } from '@/queries/use-payments';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { FindAllPaymentsSort } from '@/entities/find-all-payments-sort.enum';
import { Order } from '@/entities/pagination/order.enum';
import CVButton from '../buttons/CVButton.vue';
import Modal from '../Modal.vue';
import StatusBanner from '../StatusBanner.vue';
import ApplyCoupon from './ApplyCoupon.vue';
import PaymentDetailsModal from './PaymentDetailsModal.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { Coupon } from '@/entities/coupons/coupon.entity';
import type { Payment } from '@/entities/payments/payment.entity';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import { PaymentType } from '@/entities/payments/payment-type.enum';
import { CouponType } from '@carvia/ros-client-types';
import { Alert } from '@/services/alert.service';
import { EPSILON } from '@/constants';

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();

const error = ref<string | null>(null);
const showApplyCoupon = ref(false);
const showRefundModal = ref(false);
const refundPayment = ref<Payment | null>(null);

const { mutateAsync: removeCoupon, isPending: isRemoving } = useRemoveCoupon();

const { data: paymentsData } = usePaymentsWithQuery({
  bookingIds: [props.booking.id],
  limit: 100000,
  sort: FindAllPaymentsSort.CREATED_DATE,
  order: Order.ASC,
});
const payments = useFlattenPaginatedData(paymentsData);

const appliedCoupons = computed(() => props.booking.coupons ?? []);

const canEditPromo = computed(
  () =>
    props.booking.status !== BookingStatus.HANDED_OVER &&
    props.booking.status !== BookingStatus.CAR_RETURNED &&
    props.booking.status !== BookingStatus.FINALIZED &&
    props.booking.status !== BookingStatus.CANCELED,
);

const canEditGoodwill = computed(() => props.booking.status !== BookingStatus.CANCELED);

const canApplyCoupon = computed(() => canEditPromo.value || canEditGoodwill.value);

const canRemoveCoupon = (coupon: Coupon) => {
  if (coupon.type === CouponType.PROMO) {
    return canEditPromo.value;
  }
  if (coupon.type === CouponType.GOODWILL) {
    return canEditGoodwill.value;
  }
  return true;
};

const findCouponPayments = (coupon: Coupon): Payment[] =>
  payments.value.filter(
    (payment) => payment.paymentType === PaymentType.COUPON && payment.referenceId === coupon.code,
  );

const findInitialCouponPayment = (coupon: Coupon): Payment | undefined => {
  const payments = findCouponPayments(coupon).filter((payment) => payment.amount > 0);
  return (
    payments.find((payment) => payment.amount - (payment.refundedAmount ?? 0) > EPSILON) ??
    payments[0]
  );
};

const getCouponAmount = (coupon: Coupon): number | null => {
  if (coupon.type === CouponType.PROMO) {
    return null;
  }

  const fromPayments = findCouponPayments(coupon)
    .filter((payment) => payment.amount > 0)
    .reduce((sum, payment) => sum + payment.amount - (payment.refundedAmount ?? 0), 0);

  const fromExtras = (getCustomBookedExtras(props.booking.priceCalculation) ?? [])
    .filter((extra) => extra.isAdditional && extra.couponCode === coupon.code)
    .reduce((sum, extra) => sum + Math.abs(extra.price ?? 0), 0);

  const total = fromPayments + fromExtras;
  return Math.abs(total) > EPSILON ? total : null;
};

const getCouponErrorMessage = (err: unknown, code = ''): string => {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const description =
    (typeof data?.description === 'string' && data.description) ||
    (typeof data?.error === 'string' && data.error !== 'Bad Request' && data.error) ||
    (err as { description?: string })?.description;
  const codeParam = { code };

  switch (description) {
    case 'ALREADY_USED_COUPON':
      return t('promoAlreadyUsed', codeParam);
    default:
      return t('couponInvalid', codeParam);
  }
};

const handleRemove = async (coupon: Coupon) => {
  error.value = null;

  if (coupon.type === CouponType.REGULAR) {
    const payment = findInitialCouponPayment(coupon);
    if (!payment) {
      error.value = t('refundPaymentNotFound');
      return;
    }
    refundPayment.value = payment;
    showRefundModal.value = true;
    return;
  }

  const { isConfirmed } = await Alert.fire({
    titleText: t('removeConfirmTitle'),
    text: t('removeConfirmText', { code: coupon.code }),
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: t('cancel'),
    confirmButtonText: t('remove'),
  });
  if (!isConfirmed) return;

  try {
    await removeCoupon({ bookingId: props.booking.id, couponId: coupon.id });
  } catch (err) {
    error.value = getCouponErrorMessage(err, coupon.code);
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "coupons": "Coupons",
    "applyCoupon": "Apply coupon",
    "remove": "Remove",
    "cancel": "Cancel",
    "couponInvalid": "The coupon code {code} is invalid",
    "promoAlreadyUsed": "This customer has already used the promo code {code}",
    "removeConfirmTitle": "Remove coupon?",
    "removeConfirmText": "Remove coupon {code} and its additional extras from this booking?",
    "refundPaymentNotFound": "No coupon payment found — refund via Payments"
  },
  "de": {
    "coupons": "Gutscheine",
    "applyCoupon": "Gutschein einlösen",
    "remove": "Entfernen",
    "cancel": "Abbrechen",
    "couponInvalid": "Der Gutscheincode {code} ist ungültig",
    "promoAlreadyUsed": "Dieser Kunde hat den Aktionscode {code} bereits verwendet",
    "removeConfirmTitle": "Gutschein entfernen?",
    "removeConfirmText": "Gutschein {code} und die zugehörigen Additional Extras von dieser Buchung entfernen?",
    "refundPaymentNotFound": "Keine Gutscheinzahlung gefunden — Erstattung über Zahlungen"
  },
  "es": {
    "coupons": "Cupones",
    "applyCoupon": "Aplicar cupón",
    "remove": "Eliminar",
    "cancel": "Cancelar",
    "couponInvalid": "El código de cupón {code} no es válido",
    "promoAlreadyUsed": "Este cliente ya ha utilizado el código promocional {code}",
    "removeConfirmTitle": "¿Eliminar cupón?",
    "removeConfirmText": "¿Eliminar el cupón {code} y sus extras adicionales de esta reserva?",
    "refundPaymentNotFound": "No se encontró el pago del cupón — reembolse vía Pagos"
  }
}
</i18n>
